import type { OrderStatus } from '../../../types/order';
import type { OrderProcessStepKey } from './orderProcessSteps';
import { CUSTOMER_ORDER_PROGRESS_STATUSES } from '../customerOrderStatuses';

/**
 * The sticky action bar of the ZK card (makieta `bar()`): one hint and the
 * main action of the current step. Decision 2026-10-04 pkt 5 — statuses do
 * not block: a missing price, missing goods or a missing transport only turns
 * the hint into a warning; the action stays enabled (the backend notifies
 * Operations about missing goods when the status moves on).
 */
export type OrderStepPrimaryAction =
  | { kind: 'status'; status: OrderStatus; labelKey: string }
  | { kind: 'openOffer'; labelKey: string }
  | { kind: 'confirm'; labelKey: string }
  | { kind: 'issueWz'; labelKey: string }
  | { kind: 'deliver'; labelKey: string }
  | { kind: 'salesInvoice'; labelKey: string };

export interface OrderStepHint {
  tone: 'info' | 'warning';
  key: string;
  params?: Record<string, string | number>;
}

export interface OrderStepBar {
  hint: OrderStepHint;
  /** null when the step has no next action (closed by invoice) or the reader cannot do it. */
  primary: OrderStepPrimaryAction | null;
  /** The step is closed: the bar shows a done badge instead of an action. */
  closed?: boolean;
  /** Wstrzymane / Anulowane: no guided action, only „Zmień status ręcznie”. */
  manualOnly?: boolean;
  /**
   * The one exception to „nothing blocks”: „Stwórz ofertę” waits for at least
   * one line and every line with a quantity and sale price (makieta `bar()`
   * step 1, owner 2026-10-05). The hint says what is missing.
   */
  primaryDisabled?: boolean;
  /**
   * The main action goes ahead despite a problem (owner 2026-10-06: „Gotowe
   * do wydania” while goods are missing). The bar wears the warning colours;
   * the backend asks for a confirmation (ORDER_READY_WITH_MISSING_GOODS).
   */
  primaryWarns?: boolean;
  /**
   * A second action next to the main one (owner 2026-10-20, ZK 1/02/2026):
   * during delivery, once a WZ went out, „Wgraj fakturę za dostarczoną
   * część” — a partial invoice leaves the status, the full one closes the ZK.
   */
  secondary?: OrderStepPrimaryAction | null;
}

export interface OrderStepBarFacts {
  status: string;
  /** Lines still missing on the warehouse (stock-status `MISSING`), already formatted „Nazwa 30 m²”. */
  missing: string[];
  /** Lines without quantity or sale price (only known to readers of sale prices). */
  linesWithoutPrice: number;
  lineCount: number;
  /** Szkic: „Dodaj produkt” row is complete but not yet added to the table. */
  draftPendingRow?: boolean;
  /** Oferta: „Klient zaakceptował ofertę” ticked on the card. */
  offerAccepted: boolean;
  /** Oferta: the date of the confirmation is filled in (required once the acceptance is ticked). */
  offerAcceptanceDate: boolean;
  /** Our delivery without an outgoing truck yet (null when unknown for the reader). */
  transportMissing: boolean | null;
  /** Every line issued by WZ. */
  allIssued: boolean;
  /** At least one WZ issued for the order. */
  anyIssued: boolean;
  /** Signed WZ scan attached (null when the reader cannot see WZ attachments). */
  /**
   * Shortage notice to Operations for the CURRENT status, from the notification
   * log (orderNotificationHistory.shortageNoticeFor): sent / failed / pending
   * (the background notice has not written its row yet); null = unknown.
   */
  shortageNotice?: 'sent' | 'failed' | 'pending' | null;
  /** Customer self-pickup (`deliveryMode` CUSTOMER_PICKUP): wording only. */
  pickup?: boolean;
  /** „Prosto z fabryki do klienta”: the warehouse shortage is not a hint, so „Cały towar jest na magazynie” would be false. */
  factoryDirect?: boolean;
  /**
   * The ZK has an active advance invoice (decisions 2026-10-20 ZR.5 and
   * 2026-11-02): an ordinary invoice would be refused, so the delivered part
   * is not offered one and the last step asks for the settlement invoice.
   */
  hasAdvanceInvoice?: boolean;
  can: {
    changeStatus: boolean;
    issueWz: boolean;
    salesInvoice: boolean;
  };
}

const K = (key: string) => `orders:process.bar.${key}`;

const READY_INDEX = CUSTOMER_ORDER_PROGRESS_STATUSES.indexOf('READY_FOR_DISPATCH');

/**
 * The move to „Gotowe do wydania” already happened (status at or after it),
 * so no future move is left to promise a notice for.
 */
const readyReached = (status: string) => (
  CUSTOMER_ORDER_PROGRESS_STATUSES.indexOf(status as OrderStatus) >= READY_INDEX
);

/**
 * Missing goods after „Przekaż do realizacji”. The bar says what really
 * happened to the shortage notice of the current status (notification log,
 * never guessed from the status — owner 2026-10-06, „ekrany nie mogą sobie
 * przeczyć”): sent → „Logistyk (Valentyna) ma już powiadomienie”; failed → „nie udało się wysłać
 * … przekaż jej to inną drogą” (no hidden control is named); at „Gotowe do wydania” or later without a row yet →
 * „dostanie powiadomienie” (the card refetches the log after the move); log
 * unknown → only the missing goods. Before „Gotowe do wydania” without a
 * notice the next move still notifies („po przejściu dalej … dostanie powiadomienie”).
 */
const missingHint = (facts: OrderStepBarFacts): OrderStepHint => {
  const notice = facts.shortageNotice ?? null;
  let key = 'missingGoods';
  if (notice === 'sent') key = 'missingGoodsNotified';
  else if (notice === 'failed') key = 'missingGoodsNoticeFailed';
  else if (readyReached(facts.status)) key = notice === 'pending' ? 'missingGoodsNoticePending' : 'missingGoodsNoticeUnknown';
  return { tone: 'warning', key: K(key), params: { lines: facts.missing.join(', ') } };
};

export function getOrderStepBar(step: OrderProcessStepKey, facts: OrderStepBarFacts): OrderStepBar {
  const statusAction = (status: OrderStatus, label: string): OrderStepPrimaryAction | null =>
    facts.can.changeStatus ? { kind: 'status', status, labelKey: K(label) } : null;
  // A ZK paid by advances ends with the settlement invoice (decision 2026-11-02).
  const invoiceLabel = facts.hasAdvanceInvoice ? 'uploadSettlementInvoice' : 'uploadSalesInvoice';

  if (facts.status === 'ON_HOLD' || facts.status === 'CANCELLED') {
    return { hint: { tone: 'warning', key: K(`interrupted.${facts.status}`) }, primary: null, manualOnly: true };
  }
  switch (step) {
    case 'DRAFT': {
      const incomplete = facts.lineCount === 0 || facts.linesWithoutPrice > 0;
      return {
        hint: incomplete
          ? { tone: 'warning', key: K(facts.draftPendingRow && facts.lineCount === 0 ? 'draftPendingRow' : 'draftIncomplete') }
          : { tone: 'info', key: K('draftReady') },
        // „Stwórz ofertę” opens the Oferta step; the status moves to „Oferta
        // wysłana” only after sending it (or „Oznacz ofertę jako wysłaną”).
        primary: facts.can.changeStatus ? { kind: 'openOffer', labelKey: K('createOffer') } : null,
        primaryDisabled: incomplete,
      };
    }
    case 'OFFER': {
      // Makieta `bar()` step 2: the acceptance is ticked on the card from the
      // very first moment of the Oferta step (also while the offer is only
      // prepared, status Szkic — the customer may accept an offer sent outside
      // the system). Ticked → „Zarejestruj potwierdzenie” (date required); the
      // backend allows NEW_INQUIRY → CONFIRMED, no stop at „Oferta wysłana”.
      if (facts.offerAccepted) {
        return facts.offerAcceptanceDate
          ? {
            hint: { tone: 'info', key: K('offerAccepted') },
            primary: facts.can.changeStatus ? { kind: 'confirm', labelKey: K('registerConfirmation') } : null,
          }
          : {
            hint: { tone: 'warning', key: K('offerDateMissing') },
            primary: facts.can.changeStatus ? { kind: 'confirm', labelKey: K('registerConfirmation') } : null,
            primaryDisabled: true,
          };
      }
      if (facts.status === 'NEW_INQUIRY') {
        return {
          hint: { tone: 'info', key: K('offerPrepare') },
          primary: statusAction('QUOTE_SENT', 'markOfferSent'),
        };
      }
      return {
        hint: { tone: 'warning', key: K('offerNotAccepted') },
        primary: facts.can.changeStatus ? { kind: 'confirm', labelKey: K('registerConfirmation') } : null,
      };
    }
    case 'CONFIRMED':
      return {
        hint: facts.missing.length
          ? { tone: 'warning', key: K('confirmedMissing'), params: { lines: facts.missing.join(', ') } }
          : { tone: 'info', key: K(facts.factoryDirect ? 'factoryDirect' : 'allInStock') },
        primary: statusAction('IN_FULFILLMENT', 'toFulfillment'),
      };
    case 'FULFILLMENT':
      return {
        hint: facts.missing.length ? missingHint(facts) : { tone: 'info', key: K(facts.factoryDirect ? 'factoryDirect' : 'allInStock') },
        primary: statusAction('READY_FOR_DISPATCH', 'toReady'),
        primaryWarns: facts.missing.length > 0,
      };
    case 'READY': {
      let hint: OrderStepHint = { tone: 'info', key: K('readyFifo') };
      if (facts.missing.length) hint = missingHint(facts);
      else if (facts.transportMissing) hint = { tone: 'warning', key: K('readyNoTransport') };
      return {
        hint,
        primary: facts.can.issueWz ? { kind: 'issueWz', labelKey: K(facts.pickup ? 'issueWzForPickup' : 'issueWzAndDeliver') } : null,
      };
    }
    case 'DELIVERY': {
      // The hint names the invoice only when the bar shows its button (Logistyk and
      // a ZK with an advance do not get it).
      const invoiceAction = facts.anyIssued && facts.can.salesInvoice && !facts.hasAdvanceInvoice;
      const remainingKey = facts.status === 'PARTIALLY_DELIVERED' ? 'deliveryPartialRemaining' : 'deliveryRemaining';
      return {
        hint: !facts.anyIssued
          ? { tone: 'warning', key: K('deliveryNoWz') }
          : facts.allIssued
            ? { tone: 'info', key: K(facts.pickup ? 'pickupConfirm' : 'deliveryConfirm') }
            : { tone: 'warning', key: K(invoiceAction ? `${remainingKey}Invoice` : remainingKey) },
        primary: facts.can.changeStatus ? { kind: 'deliver', labelKey: K('confirmDelivery') } : null,
        secondary: invoiceAction ? { kind: 'salesInvoice', labelKey: K('uploadDeliveredInvoice') } : null,
      };
    }
    case 'DELIVERED':
      return {
        hint: { tone: 'info', key: K(facts.hasAdvanceInvoice ? 'deliveredNextSettlement' : 'deliveredNext') },
        primary: facts.can.salesInvoice ? { kind: 'salesInvoice', labelKey: K(invoiceLabel) } : null,
      };
    case 'INVOICE':
    default:
      if (facts.status === 'INVOICED') {
        return { hint: { tone: 'info', key: K('invoiced') }, primary: null, closed: true };
      }
      return {
        hint: { tone: 'info', key: K(facts.hasAdvanceInvoice ? 'invoiceNextSettlement' : 'invoiceNext') },
        primary: facts.can.salesInvoice ? { kind: 'salesInvoice', labelKey: K(invoiceLabel) } : null,
      };
  }
}
